import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, X, Handshake, CurrencyInr, CheckCircle } from "@phosphor-icons/react";

const STATUS_COLOR = {
  pending: "bg-[#F9E4B7] text-[#7A5A11]",
  active: "bg-[#D4E0D1] text-[#1A3317]",
  suspended: "bg-[#F4C7B9] text-[#7A2E18]",
};

export default function Franchise() {
  const [items, setItems] = useState([]);
  const [wards, setWards] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    name: "", contact_person: "", phone: "", email: "", address: "",
    territory_ward_ids: [], revenue_share_pct: 70, status: "pending", kyc_docs: [],
  });

  const load = async () => {
    const [f, w] = await Promise.all([api.get("/franchise"), api.get("/locations?type=ward")]);
    setItems(f.data); setWards(w.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/franchise", { ...form,
        revenue_share_pct: Number(form.revenue_share_pct),
        email: form.email || null });
      toast.success("Franchise created");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (id, status) => {
    try { await api.post(`/franchise/${id}/status`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell title="Franchise Management" subtitle="Module 16"
      actions={
        <button data-testid="btn-new-franchise" onClick={() => setOpen(true)}
                className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
          <Plus size={14} weight="bold"/>Onboard franchise
        </button>
      }
    >
      <div className="bg-white border border-[#E5E2D9] rounded-md">
        <table className="w-full text-sm" data-testid="franchise-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-6 py-3">Name</th>
              <th className="px-6 py-3">Contact</th>
              <th className="px-6 py-3">Territory</th>
              <th className="px-6 py-3">Customers</th>
              <th className="px-6 py-3 text-right">Share %</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="px-6 py-14 text-center text-[#8A8780]"><Handshake size={28} className="inline-block mb-3 opacity-40"/><br/>No franchises yet.</td></tr>}
            {items.map((f, i) => (
              <tr key={f.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                <td className="px-6 py-3"><div className="font-medium">{f.name}</div><div className="text-xs text-[#8A8780]">{f.contact_person}</div></td>
                <td className="px-6 py-3 font-mono text-xs text-[#5C5A55]">{f.phone}<br/>{f.email}</td>
                <td className="px-6 py-3">{f.ward_count} wards</td>
                <td className="px-6 py-3">{f.customer_count}</td>
                <td className="px-6 py-3 font-mono text-right">{f.revenue_share_pct}%</td>
                <td className="px-6 py-3"><span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${STATUS_COLOR[f.status]}`}>{f.status}</span></td>
                <td className="px-6 py-3 text-right">
                  <button onClick={() => setSelected(f)} data-testid={`payout-${f.id}`} className="text-[#2D5A27] text-xs mr-3 inline-flex items-center gap-1"><CurrencyInr size={12}/>Payout</button>
                  {f.status !== "active" && <button onClick={() => setStatus(f.id, "active")} className="text-[#2D5A27] text-xs mr-3">Activate</button>}
                  {f.status !== "suspended" && <button onClick={() => setStatus(f.id, "suspended")} className="text-[#C25D38] text-xs">Suspend</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-md w-full max-w-2xl max-h-[92vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-[#E5E2D9] flex items-center justify-between sticky top-0 bg-white">
              <div className="font-heading text-lg">Onboard franchise</div>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-4" data-testid="franchise-form">
              {[
                ["name", "Name", true], ["contact_person", "Contact person", true],
                ["phone", "Phone", true], ["email", "Email", false],
                ["address", "Address", false],
              ].map(([k, lbl, req]) => (
                <div key={k} className={k === "address" ? "col-span-2" : ""}>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">{lbl}</label>
                  <input data-testid={`fr-${k}`} required={req} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                         className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
                </div>
              ))}
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Revenue share %</label>
                <input data-testid="fr-share" type="number" min="0" max="100" value={form.revenue_share_pct}
                       onChange={e => setForm(f => ({ ...f, revenue_share_pct: e.target.value }))}
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono"/>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">KYC docs (URLs, comma-separated)</label>
                <input value={form.kyc_docs.join(",")} onChange={e => setForm(f => ({ ...f, kyc_docs: e.target.value.split(",").map(x => x.trim()).filter(Boolean) }))}
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md"/>
              </div>
              <div className="col-span-2">
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Territory wards ({form.territory_ward_ids.length})</label>
                <div className="max-h-48 overflow-y-auto border border-[#E5E2D9] rounded-md p-2 grid grid-cols-2 gap-1">
                  {wards.map(w => {
                    const on = form.territory_ward_ids.includes(w.id);
                    return (
                      <label key={w.id} className={`flex items-center gap-2 p-2 rounded text-sm cursor-pointer ${on ? "bg-[#D4E0D1]" : "hover:bg-[#F7F6F2]"}`}>
                        <input type="checkbox" checked={on} onChange={() => setForm(f => ({
                          ...f, territory_ward_ids: on ? f.territory_ward_ids.filter(x => x !== w.id) : [...f.territory_ward_ids, w.id],
                        }))} data-testid={`fr-ward-${w.id}`} className="accent-[#2D5A27]"/>
                        {w.name}
                      </label>
                    );
                  })}
                </div>
              </div>
              <button data-testid="fr-save" type="submit" className="col-span-2 h-11 bg-[#2D5A27] text-white rounded-md text-sm font-medium">
                Create franchise
              </button>
            </form>
          </div>
        </div>
      )}

      {selected && <PayoutModal franchise={selected} onClose={() => setSelected(null)}/>}
    </AppShell>
  );
}

function PayoutModal({ franchise, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const lastMonth = new Date(Date.now() - 30*86400000).toISOString().slice(0, 10);
  const [start, setStart] = useState(lastMonth);
  const [end, setEnd] = useState(today);
  const [payouts, setPayouts] = useState([]);

  const load = async () => {
    const r = await api.get(`/franchise/${franchise.id}/payouts`);
    setPayouts(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [franchise.id]);

  const compute = async () => {
    try {
      const r = await api.post(`/franchise/${franchise.id}/payout/compute?start=${start}&end=${end}`);
      toast.success(`Computed ₹${r.data.payout}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-2xl p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Payouts</div>
            <div className="font-heading text-xl">{franchise.name}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <input type="date" value={start} onChange={e => setStart(e.target.value)} className="h-10 px-3 border border-[#E5E2D9] rounded-md"/>
          <input type="date" value={end} onChange={e => setEnd(e.target.value)} className="h-10 px-3 border border-[#E5E2D9] rounded-md"/>
          <button onClick={compute} data-testid="btn-compute-payout" className="h-10 bg-[#2D5A27] text-white rounded-md text-sm font-medium">Compute</button>
        </div>
        <table className="w-full text-sm" data-testid="payouts-table">
          <thead><tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
            <th className="px-4 py-3">Period</th><th className="px-4 py-3 text-right">Gross</th><th className="px-4 py-3 text-right">Payout</th><th className="px-4 py-3 text-right">Platform fee</th>
          </tr></thead>
          <tbody>
            {payouts.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-[#8A8780]">No payouts computed.</td></tr>}
            {payouts.map(p => (
              <tr key={p.id}>
                <td className="px-4 py-3 font-mono text-xs">{p.period_start} → {p.period_end}</td>
                <td className="px-4 py-3 font-mono text-right">₹{p.gross_revenue.toFixed(2)}</td>
                <td className="px-4 py-3 font-mono text-right text-[#2D5A27] font-semibold">₹{p.payout.toFixed(2)}</td>
                <td className="px-4 py-3 font-mono text-right text-[#5C5A55]">₹{p.platform_fee.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
